/** Serial polling with backoff: publish fresh photos before announcing batch completion. */
const TERMINAL = ["done", "failed", "interrupted"];
const MIN_DELAY = 4000, MAX_DELAY = 15000, MAX_WATCH_MS = 15 * 60 * 1000;

export function watchBatch({ readJob, readPhotos, onJob, onPhotos, onComplete, onError,
  schedule = (fn, ms) => setTimeout(fn, ms), cancel = clearTimeout }) {
  let stopped = false, timer, progress = -1, consecutiveErrors = 0, delay = MIN_DELAY;
  let lastProgressAt = Date.now();
  const onVisible = () => {
    if (document.visibilityState !== "visible" || stopped) return;
    document.removeEventListener("visibilitychange", onVisible);
    tick();
  };
  const next = () => {
    if (stopped) return;
    if (Date.now() - lastProgressAt > MAX_WATCH_MS) {
      stopped = true;
      onError({ watchTimeout: true });
      return;
    }
    if (typeof document !== "undefined" && document.visibilityState === "hidden") {
      document.addEventListener("visibilitychange", onVisible);
      return;
    }
    timer = schedule(tick, delay);
    delay = Math.min(MAX_DELAY, Math.round(delay * 1.4));
  };
  const tick = async () => {
    try {
      const job = await readJob();
      if (stopped) return;
      onJob(job);
      if (job.processed !== progress || TERMINAL.includes(job.status)) {
        const photos = await readPhotos();
        if (stopped) return;
        onPhotos(photos);
        if (job.processed !== progress) { delay = MIN_DELAY; lastProgressAt = Date.now(); }
        progress = job.processed;
      }
      if (TERMINAL.includes(job.status)) {
        stopped = true;
        onComplete(job);
        return;
      }
      consecutiveErrors = 0;
    } catch (err) {
      consecutiveErrors += 1;
      if (stopped) return;
      if ([401, 403, 404].includes(err?.response?.status) || consecutiveErrors >= 8) {
        stopped = true;
        onError(err);
        return;
      }
      // Retry transient failures; never resubmit a charged edit here.
    }
    next();
  };
  tick();
  return () => { stopped = true; cancel(timer); document.removeEventListener("visibilitychange", onVisible); };
}
